import React, { useState } from "react";
import { Send, MessageCircle, Check, Home } from "lucide-react";
import { useI18n } from "../i18n/LanguageContext";
import { createRequest, buildWhatsAppLink } from "../lib/api";
import { interp } from "../lib/destinations";
import { toast } from "sonner";

export const VillaForm = ({ destinationName }) => {
  const { t } = useI18n();
  const [done, setDone] = useState(false);
  const [waLink, setWaLink] = useState("#");
  const [submitting, setSubmitting] = useState(false);
  const [f, setF] = useState({
    destination: destinationName || "", checkin: "", checkout: "", guests: "",
    bedrooms: "", budget: "", name: "", phone: "", email: "", notes: "",
  });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const inputCls = "w-full bg-transparent border border-vm-border text-vm-text placeholder:text-vm-muted px-4 py-3 text-sm focus:border-vm-accent transition-colors duration-300 rounded-none";

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.destination) { toast.error(t("request.required")); return; }
    setSubmitting(true);
    const message =
      `Villa / residence — ${f.destination}. ${f.checkin || "?"} – ${f.checkout || "?"}. ` +
      `${f.guests || "?"} guests, ${f.bedrooms || "?"} bedrooms. Budget: ${f.budget || "-"}. ${f.notes || ""}`;
    const payload = {
      name: f.name, email: f.email, phone: f.phone, contact_method: "whatsapp",
      category: "villa", urgency: "whenever", location: f.destination, date: f.checkin,
      message, source: "villa_form",
      details: { destination: f.destination, checkin: f.checkin, checkout: f.checkout, guests: f.guests, bedrooms: f.bedrooms, budget: f.budget },
    };
    try {
      await createRequest(payload);
      setWaLink(buildWhatsAppLink({ ...payload, category: t("request.categories.villa"), urgency: t("request.urgencies.whenever") }));
      setDone(true);
      toast.success(t("request.success"));
    } catch { toast.error("Error. Please try WhatsApp."); }
    finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="border border-vm-border p-10 text-center" data-testid="villa-form-success">
        <div className="mx-auto mb-6 w-14 h-14 border border-vm-accent flex items-center justify-center"><Check strokeWidth={1} size={26} className="text-vm-accent" /></div>
        <h3 className="font-serif text-3xl font-light mb-3">{t("request.success")}</h3>
        <p className="text-vm-text2 text-sm mb-8">{t("common.availability")}</p>
        <a href={waLink} target="_blank" rel="noopener noreferrer" className="vm-btn-primary inline-flex items-center gap-2 px-8 py-3 text-sm tracking-wide" data-testid="villa-continue-whatsapp"><MessageCircle strokeWidth={1.5} size={18} /> {t("request.openWhatsApp")}</a>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="border border-vm-border p-8 md:p-12" data-testid="villa-form">
      <div className="flex items-center gap-3 mb-2"><Home strokeWidth={1.25} size={18} className="text-vm-accent" /><p className="vm-label">{t("villas.label")}</p></div>
      <h3 className="font-serif text-3xl md:text-4xl font-light mb-2">{interp(t("villaForm.title"), destinationName)}</h3>
      <p className="text-vm-text2 text-sm mb-8">{t("villaForm.proposal")}</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <input value={f.destination} onChange={(e) => set("destination", e.target.value)} placeholder={t("request.category")} className={inputCls} data-testid="villa-destination" />
        <input type="number" min="1" value={f.guests} onChange={(e) => set("guests", e.target.value)} placeholder={t("common.guests")} className={inputCls} data-testid="villa-guests" />
        <input type="date" value={f.checkin} onChange={(e) => set("checkin", e.target.value)} className={inputCls} data-testid="villa-checkin" aria-label={t("villaForm.checkin")} />
        <input type="date" value={f.checkout} onChange={(e) => set("checkout", e.target.value)} className={inputCls} data-testid="villa-checkout" aria-label={t("villaForm.checkout")} />
        <input type="number" min="1" value={f.bedrooms} onChange={(e) => set("bedrooms", e.target.value)} placeholder={t("villaForm.bedrooms")} className={inputCls} data-testid="villa-bedrooms" />
        <input value={f.budget} onChange={(e) => set("budget", e.target.value)} placeholder={t("common.budget")} className={inputCls} data-testid="villa-budget" />
        <input value={f.name} onChange={(e) => set("name", e.target.value)} placeholder={t("common.name")} className={inputCls} data-testid="villa-name" />
        <input value={f.phone} onChange={(e) => set("phone", e.target.value)} placeholder={t("common.phone")} className={inputCls} data-testid="villa-phone" />
        <input value={f.email} onChange={(e) => set("email", e.target.value)} placeholder={t("common.emailLabel")} className={inputCls} data-testid="villa-email" />
      </div>
      <textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} placeholder={t("common.requirements")} rows={3} className={inputCls + " resize-none mb-6"} data-testid="villa-notes" />
      <button type="submit" disabled={submitting} className="vm-btn-primary w-full inline-flex items-center justify-center gap-2 py-4 text-xs tracking-widest uppercase disabled:opacity-50" data-testid="villa-submit"><Send strokeWidth={1.5} size={16} /> {submitting ? t("request.sending") : t("villaForm.submit")}</button>
    </form>
  );
};
